import { PlayerScenarioRunnerAPI } from "@manager/playerManager/PlayerManager";
import { inject, injectable } from "inversify";
import ReactDOM from "react-dom";
import { VintomError } from "../../api/model/error/VintomError";
import { ScenarioElement } from "../../model/playerConfiguration/ScenarioElement";
import { Subtitle } from "../../model/playerConfiguration/Subtitle";
import { PlaybackData } from "../../model/playerState/PlaybackData";
import { ScenarioElementPlayOptions } from "../../model/scenarioElementPlayOptions/ScenarioElementPlayOptions";
import { ScenarioGlobalState } from "../../model/scenarioGlobalState/ScenarioGlobalState";
import { TYPES } from "../di/types";
import { ResourcesCache } from "../loader/scenarioLoader/ResourcesCache";
import { PreferencesStorage } from "../preferencesStorage/PreferencesStorage";
import { ControlsLayerManager } from "./layerManager/ControlsLayerManager";
import { CTALayerManager } from "./layerManager/CTALayerManager";
import { IndependentAnimationLayerManager } from "./layerManager/IndependentAnimationLayerManager";
import { PlayerContainerManager } from "./layerManager/PlayerContainerManager";
import { PosterLayerManager } from "./layerManager/PosterLayerManager";
import { VideoLayerManager } from "./layerManager/VideoLayerManager";
import { ScenarioExecutor } from "./ScenarioExecutor";

@injectable()
export class AnimationDrivenScenarioExecutorImpl implements ScenarioExecutor {
  private playerAPI: PlayerScenarioRunnerAPI;
  private animationFrameHandle: number;
  private scenarioGlobalState: ScenarioGlobalState;

  constructor(
    @inject(TYPES.scenarioRunner.layerManager.PlayerContainerManager) private playerContainerManager: PlayerContainerManager,
    @inject(TYPES.scenarioRunner.layerManager.IndependentAnimationLayerManager)
    private animationLayerManager: IndependentAnimationLayerManager,
    @inject(TYPES.scenarioRunner.layerManager.CTALayerManager) private ctaLayerManager: CTALayerManager,
    @inject(TYPES.scenarioRunner.layerManager.ControlsLayerManager) private controlsLayerManager: ControlsLayerManager,
    @inject(TYPES.scenarioRunner.layerManager.PosterLayerManager) private posterLayerManager: PosterLayerManager,
    @inject(TYPES.scenarioRunner.layerManager.VideoLayerManager) private videoLayerManager: VideoLayerManager,
    @inject(TYPES.preferencesStorage.PreferencesStorage) private preferencesStorage: PreferencesStorage,
  ) {}

  setPlayerAPIDelegate(playerAPI: PlayerScenarioRunnerAPI) {
    this.playerAPI = playerAPI;
  }

  cancel(): void {
    if (this.animationFrameHandle !== undefined) {
      cancelAnimationFrame(this.animationFrameHandle);
    }
  }

  intializeAndRun(
    scenarioGlobalState: ScenarioGlobalState,
    scenarioResources: Promise<ResourcesCache>,
    options: ScenarioElementPlayOptions,
  ): Promise<void> {
    this.scenarioGlobalState = scenarioGlobalState;
    return this.initializeScenarioElement(scenarioGlobalState, scenarioResources, options.progress).then(() => {
      this.runSyncLoop();
      if (options.isAutoplay) {
        this.controlsLayerManager.play();
      } else {
        this.controlsLayerManager.pause();
      }
    });
  }

  getPlaybackData(): PlaybackData {
    const isPlaying = this.animationLayerManager.getIsPlaying();
    const time = this.animationLayerManager.getCurrentTime();
    const duration = this.animationLayerManager.getTotalTime();
    return { isPlaying, time, duration };
  }

  getScenarioGlobalState(): ScenarioGlobalState {
    return this.scenarioGlobalState;
  }

  play(): void {
    this.controlsLayerManager.play();
  }

  pause(): void {
    this.controlsLayerManager.pause();
  }

  performAction(ctaId: number, actionId: number, ...args: any[]): void {
    this.ctaLayerManager.performAction(ctaId, actionId, args);
  }

  jumpToScenarioElement(scenarioElement: ScenarioElement, progress: number): void {
    this.animationLayerManager.pause();
    cancelAnimationFrame(this.animationFrameHandle);
    this.playerAPI.loadScenarioElement(scenarioElement, { isAutoplay: true, progress });
  }

  private initializeScenarioElement(
    scenarioGlobalState: ScenarioGlobalState,
    scenarioResources: Promise<ResourcesCache>,
    progress: number,
  ): Promise<void> {
    this.resetObservers();

    this.videoLayerManager.hide();
    const scenarioPath = scenarioGlobalState.scenarioPath;
    this.posterLayerManager.newScenarioElement(scenarioGlobalState, progress);
    this.controlsLayerManager.newScenarioElement(scenarioPath);
    this.ctaLayerManager.newScenarioElement(scenarioGlobalState);
    this.animationLayerManager.newScenarioElement(scenarioPath);

    return scenarioResources.then((resourcesCache) => {
      this.posterLayerManager.resourcesLoaded(resourcesCache);

      this.animationLayerManager.resourcesLoaded(resourcesCache);
      this.animationLayerManager.loadAnimation();
      const totalTime = this.animationLayerManager.getTotalTime();

      this.ctaLayerManager.resourcesLoaded(resourcesCache, totalTime);

      if (progress) {
        const currentTime = totalTime * progress;
        /// /////
        this.animationLayerManager.moveTo(currentTime);
        this.controlsLayerManager.moveTo(currentTime);
      }
      this.controlsLayerManager.setTotalTime(totalTime);
      this.controlsLayerManager.hideLoader();
    });
  }

  private resetObservers() {
    this.videoLayerManager.registerObserver(undefined);

    this.playerContainerManager.registerObserver({
      onPlayerInteractionEnd: () => this.controlsLayerManager.userInteractionEnded(),
      onPlayerInteraction: () => this.controlsLayerManager.userInteractedWithPlayer(),
      onPlayerClick: () => this.controlsLayerManager.userClickedPlayer(),
      onPlayerContextMenu: (x: number, y: number) => this.controlsLayerManager.showPlayerInfoTooltip(x, y),
    });

    this.animationLayerManager.registerObserver({
      onEnded: () => {
        this.playerAPI.onScenarioElementEnd();
        this.controlsLayerManager.scenarioElementEnded();
      },
    });

    this.controlsLayerManager.registerObserver({
      onReloadFrame: () => {},
      onToggleDebugMode: () => {},
      onPause: () => {
        this.animationLayerManager.pause();
        this.playerAPI.onPause();
      },
      onSeekStart: () => {
        this.animationLayerManager.pause();
        this.playerAPI.onSeek();
      },
      onPlay: () => {
        this.animationLayerManager.play();
        this.playerAPI.onPlay();
      },
      /// /
      onSeekTo: (time) => this.animationLayerManager.moveTo(time),
      onVolumeChange: (volume: number) => {
        this.preferencesStorage.saveVolumePreference(volume);
        this.scenarioGlobalState = this.scenarioGlobalState.withVolume(volume);
      },
      onMutedChange: (isMuted: boolean) => {
        this.scenarioGlobalState = this.scenarioGlobalState.withMuted(isMuted);
      },
      onSubtitleSourceSelected: (subtitle: Subtitle) => {
        /* cannot happen */
      },
      onJumpToScenarioElement: (scElement: ScenarioElement, progress: number) => this.jumpToScenarioElement(scElement, progress),
      onToggleFullscreen: () => this.playerContainerManager.toggleFullscreen(),
      onSendError: (error: VintomError) => this.playerAPI.logUserError(error),
    });

    this.ctaLayerManager.registerObserver({
      onCTAShow: (cta) => this.playerAPI.onCTAShow(cta),
    });
  }

  private runSyncLoop() {
    this.animationFrameHandle = requestAnimationFrame(() => {
      try {
        const currentTime = this.animationLayerManager.getCurrentTime();
        const totalTime = this.animationLayerManager.getTotalTime();
        const isPlaying = this.animationLayerManager.getIsPlaying();

        ReactDOM.unstable_batchedUpdates(() => {
          this.ctaLayerManager.moveTo(currentTime);
          this.controlsLayerManager.moveTo(currentTime);

          this.playerAPI.onProgress(currentTime / totalTime);

          if (isPlaying) {
            this.posterLayerManager.hidePoster();
          }
        });

        this.runSyncLoop();
      } catch (e) {
        this.playerAPI.onError(new VintomError("runtimeError", e.stack.toString()));
      }
    });
  }
}
